import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { db } from '../db/client';
import { toFtsQuery } from '../db/queries';
import type { Filters, ItemRow, Kind, PoolMeta } from '../types';

const PAGE = 120;

// Порядок и подписи групп — collectibles вверху как самое ценное,
// окружение и pickup-объекты всегда в конце.
export const KIND_GROUPS: { kind: Kind; label: string }[] = [
  { kind: 'collectible', label: 'Предметы' },
  { kind: 'trinket',     label: 'Безделушки' },
  { kind: 'card',        label: 'Карты' },
  { kind: 'rune',        label: 'Руны' },
  { kind: 'pill',        label: 'Пилюли' },
  // окружение
  { kind: 'campfire',    label: 'Костры' },
  { kind: 'button',      label: 'Кнопки' },
  { kind: 'beggar',      label: 'Попрошайки' },
  { kind: 'machine',     label: 'Автоматы' },
  { kind: 'shopkeeper',  label: 'Лавочники' },
  { kind: 'obstacle',    label: 'Препятствия' },
  { kind: 'poop',        label: 'Какашки' },
  { kind: 'barrel',      label: 'Бочки' },
  // pickup
  { kind: 'chest',       label: 'Сундуки' },
  { kind: 'heart',       label: 'Сердца' },
  { kind: 'coin',        label: 'Монеты' },
  { kind: 'key',         label: 'Ключи' },
  { kind: 'bomb',        label: 'Бомбы' },
  { kind: 'battery',     label: 'Батарейки' },
  { kind: 'pickup',      label: 'Прочее' },
  // комнаты — в самом конце (только справочник, не для распознавания)
  { kind: 'room',        label: 'Комнаты' },
];

export const useItemsStore = defineStore('items', () => {
  // Дефолтный фильтр — пустой kind: показываем ВСЕ виды,
  // сгруппированные в UI по секциям.
  const filters = reactive<Filters>({
    kind: null,
    quality: [],
    pools: [],
    colors: [],
    search: '',
    sort: 'game_id',
  });

  const rows = ref<ItemRow[]>([]);
  const total = ref(0);
  const loading = ref(true);
  const error = ref<string | null>(null);
  const initialized = ref(false);
  const poolsVocab = ref<PoolMeta[]>([]);
  const dbSource = ref<'opfs' | 'memfs' | null>(null);

  let reqId = 0;

  async function init() {
    if (initialized.value) return;
    try {
      const r = await db().ready();
      total.value = r.count;
      dbSource.value = r.source;
      // Версионируем URL атласа под текущую БД — чтобы спрайты в сетке/модалке
      // не разъезжались со старым атласом из SW-кэша (см. tailwind.css).
      if (typeof document !== 'undefined' && r.version) {
        document.documentElement.style.setProperty(
          '--atlas-url', `url("/data/sprites.webp?v=${r.version}")`,
        );
      }
      poolsVocab.value = await db().poolsAll();
      initialized.value = true;
      await fetchAll();
    } catch (e) {
      error.value = (e as Error).message;
      console.error('[items.init]', e);
    }
  }

  async function fetchAll() {
    const my = ++reqId;
    loading.value = true;
    error.value = null;
    try {
      // Comlink postMessage сериализует через structuredClone, а Vue reactive
      // proxy не структурно-клонируется → передаём plain snapshot.
      const params = {
        kind: filters.kind,
        quality: [...filters.quality],
        pools: [...filters.pools],
        colors: [...filters.colors],
        search: toFtsQuery(filters.search),
        sort: filters.sort,
        limit: PAGE * 10,    // 1200 — БД маленькая, тащим всё сразу для виртуализации
        offset: 0,
      };
      const [list, n] = await Promise.all([
        db().list(params),
        db().count(params),
      ]);
      if (my !== reqId) return; // обогнал предыдущий запрос
      rows.value = list;
      total.value = n;
    } catch (e) {
      if (my === reqId) error.value = (e as Error).message;
      console.error('[items.fetch]', e);
    } finally {
      if (my === reqId) loading.value = false;
    }
  }

  // Дебаунс — 80 мс, чтобы UI чипов и набор в поиске не дёргали БД на каждый символ.
  let timer: ReturnType<typeof setTimeout> | null = null;
  watch(filters, () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fetchAll();
    }, 80);
  }, { deep: true });

  // Группировка по kind. Пустые группы не показываем (вычисляется на лету в UI).
  const groups = computed(() => {
    const byKind = new Map<Kind, ItemRow[]>();
    for (const r of rows.value) {
      const list = byKind.get(r.kind) ?? [];
      list.push(r);
      byKind.set(r.kind, list);
    }
    return KIND_GROUPS
      .map((g) => ({ ...g, items: byKind.get(g.kind) ?? [] }))
      .filter((g) => g.items.length > 0);
  });

  return { filters, rows, groups, total, loading, error, initialized, poolsVocab, dbSource, init, fetchAll };
});
